import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EventData, MatchOdds, TopicUrls } from "../../models/EventData";

export interface ExchangeSportsState {
  eventInfo: any;
  topicUrls: TopicUrls;
  betFairWSConnected: boolean;
  sportsRadarWSConnected: boolean;
  dreamWSConnected: boolean;
  exchSelectedSport: string;
  exchSports: any;
  exchEvents: any;
  sbSelectedSport: string;
  sbSports: any;
  sbEvents: any;
}

// Define the initial state
const initialState: ExchangeSportsState = {
  eventInfo: null,
  topicUrls: {
    matchOddsBaseUrl: "",
    matchOddsTopic: "",
    bookMakerBaseUrl: "",
    bookMakerTopic: "",
    fancyBaseUrl: "",
    fancyTopic: "",
    premiumBaseUrl: "",
    premiumTopic: "",
    bookMakerProvider: "",
    fancyProvider: "",
    matchOddsProvider: "",
    premiumProvider: ""
  },
  betFairWSConnected: false,
  sportsRadarWSConnected: false,
  dreamWSConnected: false,
  exchSelectedSport: "",
  exchSports: null,
  exchEvents: {},
  sbSelectedSport: "",
  sbSports: null,
  sbEvents: {},
};

// Create a slice of the state with actions and reducers
const authSlice = createSlice({
  name: "exchangeSports",
  initialState,
  reducers: {

    resetExchangeSportsState() {
      return initialState;
    },
    updateTopicUrls(state, action: PayloadAction<any>) {
      state.topicUrls = action.payload;
    },

    setBetFairConnected(state, action: PayloadAction<boolean>) {
      state.betFairWSConnected = action.payload;
    },

    setDreamConnected(state, action: PayloadAction<boolean>) {
      state.dreamWSConnected = action.payload;
    },

    setSportsRadarWSConnected(state, action: PayloadAction<boolean>) {
      state.sportsRadarWSConnected = action.payload;
    },

    setEventInfoOnSuccess(state, action: PayloadAction<any>) {
      const eInfo: EventData = action.payload;
      // For sorting runners at API call
      eInfo?.markets?.bookmakers?.map((i) =>
        i.runners.sort((a, b) => Number(a.runnerId) - Number(b.runnerId))
      );
      // For sorting markets at API call
      eInfo?.markets?.bookmakers?.sort((a, b) => {
        const aDesc = a?.marketName;
        const bDesc = b?.marketName;
        if (aDesc > bDesc) return 1;
        else if (aDesc < bDesc) return -1;
        else return 0;
      });
      state.eventInfo = eInfo;
    },

    updatePremiumOddsfromWS(state, action: PayloadAction<any>) {
      const eInfo: any = action.payload;
      const markets: MatchOdds[] = eInfo.markets
        ? eInfo.markets.matchOdds
        : eInfo.matchOdds;
      let matchOdds: any[] = [];

      const marketsMap = { ...state.eventInfo };
      // if (marketsMap?.markets?.premiumEventId !== eInfo?.current_eventId) {
      //   console.log("updatePremiumOddsfromWS: event",eInfo?.current_eventId)
      //   return;
      // }
      if (eInfo?.status.toUpperCase() === "SUSPENDED") {
        matchOdds = marketsMap?.markets ?
          marketsMap?.markets?.premiumMarkets
          : [];
      }

      // Update status
      if (marketsMap?.status) {
        marketsMap.status = eInfo.status;
      }

      // Handle market updates based on status
      if (
        markets?.length > 0 &&
        ["in_play", "upcoming"].includes(eInfo.status.toLowerCase())
      ) {
        markets.forEach((mr) => {

          // Find existing market
          const index = matchOdds.findIndex(
            (item: any) => item.marketId === mr.marketId
          );

          if (["active", "open", "suspended"].includes(mr.status.toLowerCase())) {
            if (index > -1) {
              matchOdds[index] = { ...matchOdds[index], ...mr }; // Update existing
            } else {
              matchOdds.push({ ...mr }); // Add new
            }
          }
        });
      } else {
        // Suspend all markets if none are valid
        matchOdds.forEach((mr) => {
          mr.status = "Suspended";
        });
      }

      // Update premium markets
      marketsMap.markets = {
        ...marketsMap.markets,
        // matchOdds,
        premiumMarkets: matchOdds,
      };



      // Return updated state
      return {
        ...state,
        eventInfo: marketsMap,
      };
    },

    updateEventMatchOddsWs(state, action: PayloadAction<any>) {
      let event = state.eventInfo;
      let moMarkets = action.payload;

      if (event?.eventId !== moMarkets?.current_eventId) {
        console.log("updateEventMatchOddsWs: event", moMarkets?.current_eventId)
        return;
      }

      if (event?.eventId === moMarkets?.current_eventId) {
        if (event && !!event?.markets?.matchOdds) {
          if (Array.isArray(event?.markets?.matchOdds) && event?.markets?.matchOdds.length > 1) {
            const foundIndex = event.markets.matchOdds.findIndex(
              (x: any) => x?.marketId === moMarkets.marketId
            );

            if (foundIndex !== -1) {
              event.markets.matchOdds[foundIndex] = {
                ...moMarkets,
                limits: event?.markets?.matchOdds[foundIndex]?.limits,
              };
            }
          } else {
            event.markets.matchOdds = [
              {
                ...moMarkets,
                limits: event?.markets?.matchOdds?.[0]?.limits,
              },
            ];
          }
        }
      }

      if (event?.markets?.matchOdds && event?.markets?.matchOdds.length > 0) {
        event.markets.matchOdds.sort((a: any, b: any) => {
          if (a.marketName > b.marketName) return 1;
          if (a.marketName < b.marketName) return -1;
          return 0;
        });
      }
    },


    updateBookMakerMarkets(state, action: PayloadAction<any>) {
      const bmMarket = action.payload;
      // debugger;

      // Clone the current eventInfo to avoid mutation
      const dreamEventInfo = { ...state.eventInfo };
      if (dreamEventInfo?.eventId !== bmMarket?.current_eventId) {
        console.log("updateBookMakerMarkets: event", bmMarket?.current_eventId)
        return;
      }

      // Ensure dreamEventInfo and its nested properties exist
      if (dreamEventInfo && dreamEventInfo.markets?.bookmakers) {
        const bookmakers = [...dreamEventInfo.markets.bookmakers]; // clone bookmakers

        if (Array.isArray(bmMarket)) {
          bmMarket.forEach((market) => {
            if (market && market.runners.length > 0) {
              // Sort runners immutably
              const sortedRunners = [...market.runners].sort((a: any, b: any) => {
                if (a?.sort - b?.sort !== 0) {
                  return a?.sort - b?.sort;
                }
                return a.runnerId.localeCompare(b.runnerId);
              });

              // Update market with sorted runners
              const updatedMarket = { ...market, runners: sortedRunners };

              const foundIndex = bookmakers.findIndex(
                (x: any) => x.marketId === market.marketId
              );
              if (foundIndex !== -1) {
                bookmakers[foundIndex] = {
                  ...updatedMarket,
                  limits: bookmakers[foundIndex]?.limits,
                };
              }
            }
          });
        } else if (bmMarket && bmMarket.runners.length > 0) {
          // Sort runners immutably
          const sortedRunners = [...bmMarket.runners].sort((a: any, b: any) => {
            if (a?.sort - b?.sort !== 0) {
              return a?.sort - b?.sort;
            }
            return a.runnerId.localeCompare(b.runnerId);
          });

          // Update market with sorted runners
          const updatedMarket = { ...bmMarket, runners: sortedRunners };

          const foundIndex = bookmakers.findIndex(
            (x: any) => x?.marketId === bmMarket.marketId
          );
          if (foundIndex !== -1) {
            bookmakers[foundIndex] = {
              ...updatedMarket,
              limits: bookmakers[foundIndex]?.limits,
            };
          }
        }

        // Sort bookmakers immutably
        dreamEventInfo.markets.bookmakers = [...bookmakers].sort((a: any, b: any) => {
          return a.marketName.localeCompare(b.marketName);
        });
      }

      // Ensure the updated eventInfo is returned as part of the state
      // return {
      //   ...state,
      //   eventInfo: dreamEventInfo,
      // };
    },


    updateFancyMarkets(state, action: PayloadAction<any>) {
      const fmMarket = action.payload;

      // Clone the current eventInfo to avoid direct mutation
      const dreamEventInfo = { ...state.eventInfo };

      let isTommy: boolean = dreamEventInfo.markets.fancyTopic.includes("tommy")

      if (dreamEventInfo?.eventId !== fmMarket?.current_eventId) {
        console.log("updateFancyMarkets: event", fmMarket?.current_eventId)
        return;
      }

      // Initialize fancyMarkets safely
      const fancyMarkets = dreamEventInfo.markets?.fancyMarkets
        ? [...dreamEventInfo.markets.fancyMarkets]
        : [];

      // Create a new array with updated data
      const data = fmMarket.map((market: any) => ({
        ...market,
        limits: isTommy ? fancyMarkets[0]?.limits || null : market?.limits,
      }));

      // Update fancyMarkets with the new data
      const updatedFancyMarkets = [...data].sort((a: any, b: any) => {
        const aDesc = a.marketName;
        const bDesc = b.marketName;
        if (aDesc > bDesc) return 1;
        else if (aDesc < bDesc) return -1;
        return 0;
      });

      // Update dreamEventInfo with the new fancyMarkets array
      dreamEventInfo.markets = {
        ...dreamEventInfo.markets,
        fancyMarkets: updatedFancyMarkets,
      };

      return {
        ...state,
        eventInfo: dreamEventInfo,
      };
    },

    updateExchEventOddsWs(state, action: PayloadAction<any>) {
      const { sport_key, current_eventId, marketId, ...marketData } = action.payload;

      if (!sport_key || !current_eventId || !marketId) return;

      const event = state.exchEvents[sport_key]?.[current_eventId];
      const matchOdds = event?.markets?.matchOdds;

      // If matchOdds not found or not an array, skip
      if (!Array.isArray(matchOdds)) return;

      // Find the existing market index
      const index = matchOdds.findIndex((m: any) => m.marketId === marketId);
      if (index === -1) return;

      // Update the found market, preserve existing limits
      matchOdds[index] = {
        ...marketData,
        marketId,
        limits: matchOdds[index]?.limits || {},
      };

      // Optional: sort by marketName for consistent order
      matchOdds.sort((a: any, b: any) => a.marketName?.localeCompare(b.marketName));

      // Trigger Redux reactivity by replacing updated objects
      state.exchEvents[sport_key] = {
        ...state.exchEvents[sport_key],
        [current_eventId]: {
          ...event,
          markets: {
            ...event.markets,
            matchOdds: [...matchOdds],
          },
        },
      };
    },

    setExchSports(state, action: PayloadAction<any>) {
      state.exchSports = action.payload;
    },

    setExchEvents(state, action: PayloadAction<any>) {
      state.exchEvents = action.payload;
    },

    updateExchEvents(state, action: PayloadAction<any>) {
      state.exchEvents[action.payload.key] = action.payload.data;
    },

    setExchSelectedSport(state, action: PayloadAction<any>) {
      state.exchSelectedSport = action.payload;
    },

    setSBSports(state, action: PayloadAction<any>) {
      state.sbSports = action.payload;
    },

    setSBEvents(state, action: PayloadAction<any>) {
      state.sbEvents = action.payload;
    },

    setSBSelectedSport(state, action: PayloadAction<any>) {
      state.sbSelectedSport = action.payload;
    },

    updateSBEvents(state, action: PayloadAction<any>) {
      state.sbEvents[action.payload.key] = action.payload.data;
      state.sbEvents[action.payload.key].totalCount = action.payload.totalCount;
    },

    updateSBEventOddsWs(state, action: PayloadAction<any>) {
      const { sport_key, current_eventId, homeScore, awayScore, status, markets } = action.payload;

      if (!sport_key || !current_eventId) return;

      const event = state.sbEvents[sport_key]?.[current_eventId];
      if (!event) return;

      const matchOdds = event.markets?.matchOdds;
      if (!Array.isArray(matchOdds) || matchOdds.length === 0) return;

      const updatedMatchOdds = [...matchOdds];

      // Only update the first market
      const currentMarket = updatedMatchOdds[0];
      if (!currentMarket) return;

      if (markets?.matchOdds?.length > 0) {
        const updatedMarket = markets.matchOdds.find(
          (m: any) => m.marketId === currentMarket.marketId
        );

        if (updatedMarket) {
          updatedMatchOdds[0] = {
            ...currentMarket,
            ...updatedMarket, // Overwrite with updated fields
            limits: currentMarket.limits || {}, // Always preserve limits from old
          };
        }
      }

      // Final update back to sbEvents
      state.sbEvents[sport_key] = {
        ...state.sbEvents[sport_key],
        [current_eventId]: {
          ...event,
          homeScore: homeScore ?? event.homeScore,
          awayScore: awayScore ?? event.awayScore,
          status: status ?? event.status,
          markets: {
            ...event.markets,
            matchOdds: updatedMatchOdds,
          },
        },
      };
    }

    // Define other reducers here if needed
  },
});

// Export the actions generated by createSlice
export const { updateTopicUrls, setEventInfoOnSuccess, setSportsRadarWSConnected, setDreamConnected, setBetFairConnected, updatePremiumOddsfromWS, updateEventMatchOddsWs, updateBookMakerMarkets, updateFancyMarkets, resetExchangeSportsState,
  setExchSports, setExchEvents, setExchSelectedSport, updateExchEvents, updateExchEventOddsWs, setSBSports, setSBEvents, setSBSelectedSport, updateSBEvents, updateSBEventOddsWs
} = authSlice.actions;

// Export the reducer to be used in the store
export default authSlice.reducer;
